// The blog (content/blog). The index and each post render as React Server Components in the `rsc`
// environment, like the docs pages (src/server/functions/DocsPage.tsx): the browser gets neither the
// compiled posts nor the artwork code.

import { blog } from "fumadocs-mdx:collections/server";
import { notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { renderServerComponent } from "@tanstack/react-start/rsc";
import BlogIndex from "src/components/blog/BlogIndex";
import BlogPostArticle from "src/components/blog/BlogPostArticle";
import { getBlogIndexJsonLd, getBlogPostJsonLd } from "src/lib/BlogJsonLd";
import { getBlogPostSlug } from "src/lib/BlogPosts";
import { serializeJsonLd } from "src/lib/LandingJsonLd";
import { withHydrationMarker } from "src/lib/ServerContent";
import { BLOG_POSTS, getBlogPostSummary } from "src/server/blog/BlogIndex";
import { renderCachedServerComponent } from "src/server/FlightCache";

export const getBlogIndex = createServerFn({ method: "GET" }).handler(() =>
  renderCachedServerComponent("blog", () =>
    renderServerComponent(
      withHydrationMarker(
        <>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: serializeJsonLd(getBlogIndexJsonLd(BLOG_POSTS)) }}
          />
          <BlogIndex posts={BLOG_POSTS} />
        </>,
      ),
    ),
  ),
);

export const getBlogPostPage = createServerFn({ method: "GET" })
  .validator((slug: string) => slug)
  .handler(async ({ data: slug }) => {
    const post = getBlogPostSummary(slug);
    const entry = blog.find((candidate) => getBlogPostSlug(candidate.info.path) === slug);
    if (!post || !entry) throw notFound();
    const { body } = await entry.load();
    return {
      post,
      content: await renderCachedServerComponent(`blog:${slug}`, () =>
        renderServerComponent(
          withHydrationMarker(
            <>
              <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: serializeJsonLd(getBlogPostJsonLd(post)) }}
              />
              <BlogPostArticle
                post={post}
                Body={body}
                morePosts={BLOG_POSTS.filter((other) => other.slug !== slug).slice(0, 2)}
              />
            </>,
          ),
        ),
      ),
    };
  });
